import React, { useCallback, useEffect, useMemo, useState } from "react";
import DetailedList from "../../../HelperComponents/DetailedList";
import { getPDH } from "../../../Service/Services";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";
import moment from "moment";

interface ProfessionalDevHistoryProps {
  user: any;
}

const styles = {
  container: {
    fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
    padding: "12px",
  },
  card: {
    backgroundColor: "hsl(var(--background))",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "hsl(var(--primary))",
    paddingBottom: "8px",
    borderBottom: "1px solid hsl(var(--border))",
    marginBottom: "12px",
  },
};

const ProfessionalDevHistory = (props: ProfessionalDevHistoryProps) => {
  const [loading, setLoading] = useState(false);
  const [pdhItems, setPdhItems] = useState<any[]>([]);

  const fetchPDH = useCallback(async () => {
    try {
      setLoading(true);
      if (!props.user?.FullName) {
        setPdhItems([]);
        return;
      }

      const response = await getPDH(props.user.FullName);
      const items = response.data?.items ?? [];

      const mapped = items.map((item: any) => {
        const startValue = item.TrainingsCalendarID_x003a_Start_?.LookupValue;
        const endValue = item.TrainingsCalendarID_x003a_End_x0?.LookupValue;

        let durationMinutes: number | null = null;
        if (startValue && endValue) {
          const start = moment(startValue);
          const end = moment(endValue);
          if (start.isValid() && end.isValid()) {
            const duration = moment.duration(end.diff(start));
            durationMinutes = duration.asMinutes();
          }
        }

        const instructorRaw = item.Instructor;
        const instructorArray = Array.isArray(instructorRaw)
          ? instructorRaw
          : instructorRaw
            ? [instructorRaw]
            : [];

        return {
          id: item.ID,
          course: item.TrainingsCalendarID_x003a_Title?.LookupValue ?? "",
          eventDate: item.EventDate ?? "",
          completed: item.Completed1 ? "Yes" : "No",
          isCompleted: !!item.Completed1,
          mandatory: item.Mandatory ?? "",
          instructor: instructorArray,
          hasTimeBeenEntered: item.HasTimeBeenEntered ? "Yes" : "No",
          start: startValue ?? "",
          end: endValue ?? "",
          created: item.Created ?? "",
          durationMinutes,
        };
      });

      setPdhItems(mapped);
    } catch (error) {
      console.error("Error fetching professional development history:", error);
    } finally {
      setLoading(false);
    }
  }, [props.user?.FullName]);

  useEffect(() => {
    if (!props.user) return;
    fetchPDH();
  }, [props.user, fetchPDH]);

  const fields = [
    {
      key: "eventDate",
      name: "Event Date",
      fieldName: "eventDate",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return moment(item.eventDate).format("MM/DD/YYYY");
      },
    },
    {
      key: "course",
      name: "Course/ Event",
      fieldName: "course",
      minWidth: 200,
      maxWidth: 250,
      isResizable: true,
      isSortable: true,
      headerClassName: "detailedListHeader",
      hasHeaderMenu: true,
      className: "detailedListColumn",
    },
    {
      key: "completed",
      name: "Completed",
      fieldName: "completed",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return (
          <div className={item.completed === "Yes" ? "pillGreen" : "pillRed"}>
            {item.completed === "Yes" ? "Yes" : "No"}
          </div>
        );
      },
    },
    {
      key: "duration",
      name: "Duration",
      fieldName: "duration",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        if (typeof item.durationMinutes === "number") {
          const hours = Math.floor(item.durationMinutes / 60);
          const minutes = Math.round(item.durationMinutes % 60);
          return `${hours}h ${minutes}m`;
        }
        return "";
      },
    },
    {
      key: "instructor",
      name: "Instructor",
      fieldName: "instructor",
      minWidth: 200,
      maxWidth: 250,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        if (!item.instructor || item.instructor.length === 0) {
          return null;
        }

        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {item.instructor.map((instr: any, index: number) => (
              <div key={index} className="pillBlue">
                {instr?.LookupValue ?? ""}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "mandatory",
      name: "Mandatory",
      fieldName: "mandatory",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return (
          <div className={item.mandatory === "Yes" ? "pillGreen" : "pillRed"}>
            {item.mandatory === "Yes" ? "Yes" : "No"}
          </div>
        );
      },
    },
    {
      key: "hasTimeBeenEntered",
      name: "Time Entered",
      fieldName: "hasTimeBeenEntered",
      minWidth: 100,
      maxWidth: 120,
      isResizable: false,
      isSortable: true,
      headerClassName: "detailedListHeader",
      className: "detailedListColumn",
      onRender: (item: any) => {
        return (
          <div
            className={
              item.hasTimeBeenEntered === "Yes" ? "pillGreen" : "pillRed"
            }
          >
            {item.hasTimeBeenEntered === "Yes" ? "Yes" : "No"}
          </div>
        );
      },
    },
  ];

  const summary = useMemo(() => {
    let completedMinutes = 0;
    let completedTrainings = 0;

    pdhItems.forEach((item: any) => {
      if (item.isCompleted && typeof item.durationMinutes === "number") {
        completedMinutes += item.durationMinutes;
        completedTrainings += 1;
      }
    });

    const totalHours = completedMinutes / 60;
    const totalTrainings = pdhItems.length;
    const completionPercent =
      totalTrainings > 0
        ? Math.round((completedTrainings / totalTrainings) * 100)
        : 0;

    return {
      totalHours,
      completionPercent,
      completedTrainings,
      totalTrainings,
    };
  }, [pdhItems]);

  return (
    <div className="contentContainer">
      {loading && <LoadingSpinner />}
      <div key={1} className="content">
        {!loading && (
          <>
            <h2 className="title">Professional Development History</h2>
            <div className="contentCard">
              <div style={styles.container}>
                <div>
                  <div style={styles.card}>
                    <h3 style={styles.sectionTitle}>
                      Professional Development History
                    </h3>
                    <DetailedList
                      items={pdhItems}
                      columns={fields}
                      listType="shimmered"
                      selectionMode="none"
                    />
                    {pdhItems.length > 0 && (
                      <div
                        style={{
                          marginTop: "12px",
                          display: "flex",
                          padding: "12px",
                          justifyContent: "flex-end",
                          fontSize: "18px",
                          background: "hsl(var(--primary))",
                          color: "#FFF",
                          fontWeight: "600",
                        }}
                      >
                        <span style={{ marginRight: "16px" }}>
                          Total Hours: {summary.totalHours.toFixed(2)}
                        </span>
                        <span>
                          Completed: {summary.completionPercent}% (
                          {summary.completedTrainings}/{summary.totalTrainings})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfessionalDevHistory;
